import { z } from "zod";

import { API_VERSION, MAX_ENTITY_SLUG_LENGTH } from "./common";
import { recipeTypeSchema } from "./recipe-domain";
import { machineTypeSchema } from "./machine-domain";
export { recipeTypeSchema, type RecipeType } from "./recipe-domain";
import { ingredientCategorySchema } from "./ingredient-domain";

export {
  ingredientCategorySchema,
  ingredientWriteSchema,
} from "./ingredient-domain";
export type {
  IngredientCategory,
  IngredientWrite,
  ParsedIngredientWrite,
} from "./ingredient-domain";

const compositionValueSchema = z.string();

export const ingredientSchema = z.object({
  id: z.number().int().positive(),
  slug: z.string().min(1),
  name: z.string().min(1),
  source: z.string().nullable(),
  notes: z.string().nullable(),
  aliases: z.array(z.string()).nullable(),
  category: ingredientCategorySchema,
  sugarsPct: compositionValueSchema,
  lactosePct: compositionValueSchema,
  fatPct: compositionValueSchema,
  saturatedFatPct: compositionValueSchema,
  transFatPct: compositionValueSchema,
  dairyFatPct: compositionValueSchema,
  cocoaFatPct: compositionValueSchema,
  cocoaSolidsNonfatPct: compositionValueSchema,
  waterPct: compositionValueSchema,
  pod: compositionValueSchema,
  pac: compositionValueSchema,
  alcoholPct: compositionValueSchema,
  energyKcal: compositionValueSchema,
  proteinPct: compositionValueSchema,
  carbPct: compositionValueSchema,
  fiberPct: compositionValueSchema,
  emulsifierPct: compositionValueSchema,
  stabilizerPct: compositionValueSchema,
  isOwned: z.boolean(),
});

const optionalSearchString = (max: number) =>
  z.preprocess(
    (value) => (value === "" || value === null ? undefined : value),
    z.string().trim().max(max).optional(),
  );

const pageSizeSchema = z.preprocess(
  (value) =>
    value === undefined || value === null || value === ""
      ? undefined
      : Number(value),
  z.number().int().min(1).max(100).default(40),
);

export const ingredientsQuerySchema = z
  .object({
    q: optionalSearchString(120),
    search: z.preprocess(
      (value) => (value === "" || value === null ? undefined : value),
      z.enum(["text", "semantic"]).default("text"),
    ),
    category: z.preprocess(
      (value) => (value === "" || value === null ? undefined : value),
      ingredientCategorySchema.optional(),
    ),
    source: optionalSearchString(80),
    limit: pageSizeSchema,
    cursor: optionalSearchString(512),
  })
  .strict()
  .superRefine((input, context) => {
    if (input.search === "semantic" && (!input.q || input.q.length < 3)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Semantic searches require at least 3 characters",
        path: ["q"],
      });
    }
  });

export const ingredientsResponseSchema = z.object({
  apiVersion: z.literal(API_VERSION),
  data: z.array(ingredientSchema),
  page: z.object({
    total: z.number().int().nonnegative(),
    hasMore: z.boolean(),
    nextCursor: z.string().nullable(),
  }),
});

export const resolveIngredientsRequestSchema = z.object({
  slugs: z
    .array(z.string().trim().min(1).max(MAX_ENTITY_SLUG_LENGTH))
    .min(1)
    .max(50),
});

export const resolveIngredientsResponseSchema = z.object({
  apiVersion: z.literal(API_VERSION),
  data: z.array(ingredientSchema),
});

const fractionSchema = z.number().min(0).max(1);

export const ingredientUsageRecipeSchema = z.object({
  slug: z.string().min(1),
  name: z.string().min(1),
  type: recipeTypeSchema,
});

export const similarIngredientSchema = z.object({
  slug: z.string().min(1),
  name: z.string().min(1),
  source: z.string().nullable(),
  externalId: z.string().nullable(),
  recipeCount: z.number().int().nonnegative(),
});

export const ingredientDetailSchema = ingredientSchema.extend({
  externalId: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  usageCount: z.number().int().nonnegative(),
  usageRecipes: z.array(ingredientUsageRecipeSchema),
  similarIngredients: z.array(similarIngredientSchema),
});

export const ingredientResponseSchema = z.object({
  apiVersion: z.literal(API_VERSION),
  data: ingredientDetailSchema,
});

export const deleteIngredientResponseSchema = z.object({
  apiVersion: z.literal(API_VERSION),
  data: z.object({ deleted: z.literal(true), slug: z.string() }),
});

export const machineSchema = z.object({
  id: z.number().int().positive(),
  slug: z.string().min(1),
  name: z.string().min(1),
  volumeMl: z.number().int().positive(),
  type: machineTypeSchema,
  isOwned: z.boolean(),
});

export const targetRangeSchema = z.object({
  min: z.number(),
  max: z.number(),
});

const targetFractionRangeSchema = z.object({
  min: fractionSchema,
  max: fractionSchema,
});

export const targetProfileConfigSchema = z.object({
  servingTemperatureC: targetRangeSchema.optional(),
  relativeSweetness: targetRangeSchema.optional(),
  totalSolidsPct: targetRangeSchema.optional(),
  fatPct: targetRangeSchema.optional(),
  saturatedFatRatio: targetFractionRangeSchema.optional(),
  milkFatPct: targetRangeSchema.optional(),
  msnfPct: targetRangeSchema.optional(),
  sugarsPct: targetRangeSchema.optional(),
  stabilizersPct: targetRangeSchema.optional(),
  stabilizersPerWaterPct: targetRangeSchema.optional(),
  emulsifiersPct: targetRangeSchema.optional(),
  alcoholPct: targetRangeSchema.optional(),
});

export const targetProfileSchema = z.object({
  id: z.number().int().positive(),
  slug: z.string().min(1),
  name: z.string().min(1),
  recipeTypes: z.array(recipeTypeSchema),
  description: z.string().nullable(),
  link: z.string().nullable(),
  config: targetProfileConfigSchema,
  isDefault: z.boolean(),
  isOwned: z.boolean(),
});

export const servingPresetSchema = z.object({
  id: z.number().int().positive(),
  slug: z.string().min(1),
  name: z.string().min(1),
  description: z.string().nullable(),
  temperatureC: z.number().finite(),
  isDefault: z.boolean(),
  isOwned: z.boolean(),
});
export type ServingPresetDto = z.infer<typeof servingPresetSchema>;

export const starterRecipeSchema = z.object({
  name: z.string().min(1),
  description: z.string(),
  type: recipeTypeSchema,
  sourceUrl: z.string().url().nullable(),
  ingredients: z.array(
    z.object({
      ingredient: ingredientSchema,
      amountGrams: z.number().positive(),
    }),
  ),
});

export const bootstrapResponseSchema = z.object({
  apiVersion: z.literal(API_VERSION),
  serverTime: z.string().datetime(),
  authenticated: z.boolean(),
  featureFlags: z.record(z.string(), z.boolean()),
  runtime: z.object({
    minimumSupportedVersion: z
      .string()
      .regex(/^\d+\.\d+\.\d+(?:[-+][0-9A-Za-z.-]+)?$/)
      .nullable(),
    maintenance: z.object({
      enabled: z.boolean(),
      message: z.string().nullable(),
    }),
  }),
  machines: z.array(machineSchema),
  targetProfiles: z.array(targetProfileSchema),
  servingPresets: z.array(servingPresetSchema).optional(),
  starterRecipes: z.array(starterRecipeSchema).optional(),
});

export type IngredientDto = z.infer<typeof ingredientSchema>;
export type IngredientsQuery = z.input<typeof ingredientsQuerySchema>;
export type ParsedIngredientsQuery = z.output<typeof ingredientsQuerySchema>;
export type IngredientsResponse = z.infer<typeof ingredientsResponseSchema>;
export type ResolveIngredientsRequest = z.input<
  typeof resolveIngredientsRequestSchema
>;
export type ParsedResolveIngredientsRequest = z.output<
  typeof resolveIngredientsRequestSchema
>;
export type ResolveIngredientsResponse = z.infer<
  typeof resolveIngredientsResponseSchema
>;
export type IngredientDetailDto = z.infer<typeof ingredientDetailSchema>;
export type SimilarIngredientDto = z.infer<typeof similarIngredientSchema>;
export type IngredientResponse = z.infer<typeof ingredientResponseSchema>;
export type DeleteIngredientResponse = z.infer<
  typeof deleteIngredientResponseSchema
>;
export type MachineDto = z.infer<typeof machineSchema>;
export type TargetProfileDto = z.infer<typeof targetProfileSchema>;
export type StarterRecipeDto = z.infer<typeof starterRecipeSchema>;
export type BootstrapResponse = z.infer<typeof bootstrapResponseSchema>;
